import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Pagination,
    TextField,
    Typography,
} from "@mui/material";
import {CancelButton} from "../../../components/common/ActionButtons";
import {
    Header,
} from "./GroupDetails.styled";
import axios from "axios";
import {ReactComponent as UsersPlusIcon} from "../../../assets/icons/users-plus.svg";
import {useNavigate, useParams} from "react-router-dom";
import {User} from "../../../models/models";
import {ReactComponent as UserDeleteIcon} from "../../../assets/icons/user-minus.svg";
import {parseError} from "../../../errors/http.service";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import { useTranslation } from 'react-i18next';
import ResponsiveTable, { ResponsiveTableColumn } from "../../../components/common/ResponsiveTable";
import {StyledTableContainer} from "../../../components/common/TableContainer.styled";
import FieldHelpSuffix from "../../../components/common/FieldHelpSuffix";
import BackButton from '../../../components/common/BackButton';
import {groupsService} from "../../../services/groups.service";

const fetchAvailableUsers = async (page = 0, size = 10, searchTerm = "") => {
    try {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('size', size.toString());

        if (searchTerm) {
            params.append('q', searchTerm);
        }

        const result = await axios.get(`/users`, {params});
        return {
            content: result.data.content as User[],
            totalElements: result.data.totalElements || 0
        };
    } catch (error) {
        console.error("Error fetchin users:", error);
        return {content: [], totalElements: 0};
    }
};

const GroupDetails = () => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const navigate = useNavigate();

    const {id} = useParams();

    const [groupUsers, setGroupUsers] = useState<User[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [groupName, setGroupName] = useState<string>("");

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [error, setError] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(7);
    const [totalElements, setTotalElements] = useState(0);

    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const updateQueryParam = () => {
        const params = new URLSearchParams(location.search);
        params.set("name", groupName);
        navigate(`${location.pathname}?${params.toString()}`, {replace: true});
    };

    if (!id) {
        navigate("/admin/group/list")
    }

    const queryParams = new URLSearchParams(location.search);
    const defaultName = queryParams.get("name");

    const removeUserFromGroup = useCallback(async (userId: number) => {
        try {
            await groupsService.removeUserFromGroup(Number(id), userId);
            setIsDialogOpen(false);

            const group = await groupsService.fetchGroupUsers(Number(id));
            setGroupUsers(group);
            showSnackbar("Użytkownik został usunięty z grupy.", "success");
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    }, [id, showSnackbar]);

    const columns: ResponsiveTableColumn<User>[] = React.useMemo(() => [
        {
            id: 'email',
            header: t('groupDetails.tableHeaders.email'),
            render: (u) => u.email,
        },
        {
            id: 'actions',
            header: t('groupDetails.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (u) => (
                <Button
                    variant={'outlined'}
                    onClick={() => removeUserFromGroup(u.userId as number)}
                >
                    <UserDeleteIcon aria-hidden={true} focusable={false}/>
                    <span style={{marginLeft: '8px'}}>{t('groupDetails.removeUserButton')}</span>
                </Button>
            ),
        },
    ], [t, removeUserFromGroup]);

    const fetchAvailableUsersData = useCallback(async () => {
        if (!id) return;

        try {
            const groupUsers = await groupsService.fetchGroupUsers(Number(id));
            const groupUserEmails = new Set(groupUsers?.map(user => user.email));

            const result = await fetchAvailableUsers(currentPage - 1, pageSize, debouncedSearchTerm);
            const filteredUsers = result.content.filter((user: User) => !groupUserEmails.has(user.email));

            setGroupUsers(groupUsers);
            setUsers(filteredUsers);
            setTotalElements(result.totalElements);
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    }, [id, currentPage, pageSize, debouncedSearchTerm, showSnackbar]);

    useEffect(() => {
        if (defaultName) {
            setGroupName(defaultName);
        }

        fetchAvailableUsersData();
    }, [defaultName, fetchAvailableUsersData]);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 150);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    useEffect(() => {
        fetchAvailableUsersData();
    }, [debouncedSearchTerm, fetchAvailableUsersData]);


    const addUserToGroup = async (userId: number) => {
        try {
            await axios.post(`/groups/${id}/users`, { userId: [userId] });
            setIsDialogOpen(false);

            const group = await groupsService.fetchGroupUsers(Number(id));
            setGroupUsers(group);
            await fetchAvailableUsersData();
            showSnackbar("Użytkownik został dodany do grupy.", "success");
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const updateGroupName = async () => {
        try {
            await axios.patch(`/groups`, {id, name: groupName});
            setGroupName(groupName);
            updateQueryParam();
            setError("");
        } catch (error: unknown) {
            showSnackbar(parseError(error), "error");
        }
    };

    return (
        <StyledTableContainer role="region" aria-labelledby="group-details-title">
            <Header>
                <div>
                    <BackButton/>
                </div>
                <div style={{display: "flex", alignItems: "center", gap: "16px"}}>
                    <h2 id="group-details-title">{t('groupDetails.title')}</h2>
                    <TextField
                        variant="outlined"
                        size="small"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        inputProps={{
                            'aria-label': t('groupDetails.title') as string,
                        }}
                        InputProps={{
                            endAdornment: (
                                <FieldHelpSuffix
                                    asAdornment
                                    title={t('groupDetails.help.groupName')}
                                    ariaLabel={t('groupDetails.help.groupName') as string}
                                />
                            ),
                        }}
                        sx={{ minWidth: 240 }}
                    />
                    {groupName && defaultName !== groupName && (
                        <Button variant="outlined" onClick={updateGroupName}>
                            {t('groupDetails.saveButton')}
                        </Button>
                    )}
                </div>
                {error && <Typography color="error">{error}</Typography>}
            </Header>
            <Button
                startIcon={<UsersPlusIcon aria-hidden={true} focusable={false}/>}
                sx={{
                    width: { xs: "100%", sm: "max-content" },
                    display: "flex",
                    marginLeft: { xs: 0, sm: "auto" },
                    marginBottom: "16px"
                }}
                variant="contained" color="primary"
                onClick={() => setIsDialogOpen(true)}
                aria-label={t('groupDetails.addUserButton') as string}
            >
                {t('groupDetails.addUserButton')}
            </Button>
            <ResponsiveTable
                columns={columns}
                data={groupUsers}
                getRowKey={(u) => u.userId as number}
                emptyState={t('groupDetails.emptyState')}
                ariaLabel={t('groupDetails.title') as string}
            />

            <Dialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                maxWidth="md"
                aria-labelledby="add-user-dialog-title"
                PaperProps={{
                    sx: {
                        height: "auto",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "300px"
                    }
                }}
            >
                <DialogTitle id="add-user-dialog-title">{t('groupDetails.addUserDialog.title')}</DialogTitle>

                <DialogContent
                    sx={{
                        padding: "32px 48px !important",
                        paddingBottom: "0 !important",
                        width: "450px",
                        display: "flex",
                        flexDirection: "column"
                    }}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label={t('groupDetails.addUserDialog.searchLabel')}
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{marginBottom: "16px"}}
                        inputProps={{
                            role: 'searchbox',
                            'aria-label': t('groupDetails.addUserDialog.searchLabel') as string,
                        }}
                    />

                    <List
                        sx={{
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            padding: "8px",
                            background: "#fafafa",
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {users.length === 0 ? (
                            debouncedSearchTerm !== "" ? (
                                <ListItem>
                            <ListItemText primary={t('groupDetails.addUserDialog.noUsersMessage')}/>
                                </ListItem>
                            ) : null
                        ) : (
                            users.map(user => (
                                <ListItem
                                    key={user.email}
                                    button
                                    onClick={() => addUserToGroup(user.id as number)}
                                    sx={{
                                        borderRadius: "4px",
                                        "&:hover": {backgroundColor: "#f0f0f0"}
                                    }}
                                >
                                <ListItemText primary={user.email}/>
                                </ListItem>
                            ))
                        )}
                    </List>

                    {totalElements > pageSize && (
                        <Box sx={{display: 'flex', justifyContent: 'center', marginTop: "20px"}}>
                            <Pagination
                                count={Math.ceil(totalElements / pageSize)}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{padding: "20px", paddingTop: "0 !important"}}>
                    <CancelButton onClick={() => setIsDialogOpen(false)}>{t('groupDetails.cancelButton')}</CancelButton>
                </DialogActions>
            </Dialog>
        </StyledTableContainer>
    );
};

export default GroupDetails;
